import { useCallback, useState, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Trans } from "react-i18next"

import {
  Box,
  Stepper,
  Typography,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  useTheme,
  Backdrop,
  CircularProgress,
  StepConnector,
  Button,
} from "@mui/material"

import { useTranslation } from "providers/i18n"
import { useAppUI } from "providers/ui"
import { useVenue } from "providers/venue"

import { DirectionSearchContainer } from "containers/DirectionSearchContainer"

import { useDirectionOverview } from "hooks/direction/useDirectionOverview"
import { useDirectionOverviewMap } from "hooks/direction/useDirectionOverviewMap"

import { getIcon } from "utils/icon"
import _ from "lodash"

import {
  DirectionSummary,
  DirectionStepItem,
  DirectionCardActions,
} from "components/domains/direction"
import { SwipeableDrawer } from "components/SwipeableDrawer"
import { OrdinalSelectMenu } from "components/OrdinalSelector/OrdinalSelectMenu"
import { OrdinalSelector } from "components/OrdinalSelector"
import { CloseIcon, OriginMarkerIcon } from "components/icons"
import { MyLocationButton } from "components/Button"
import { DestinationMarkerIcon } from "components/icons"

export const DirectionOverviewPage = () => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const { isMultiOrdinalVenue } = useVenue()

  const {
    isCalculatingRoute,
    overviewSteps,
    duration,
    distance,
    currentOrdinal,
    originData,
    originName,
    destinationData,
    destinationName,
    elevatorPreferredMode,
    relatedLevelWithSteps,
    scannedLocations,
    userLocation,
    route,
    changeOrdinal,
    toggleElevatorPreference,
    startNavigate,
  } = useDirectionOverview()

  useDirectionOverviewMap(route, currentOrdinal)

  const { t } = useTranslation()

  const { goBack } = useAppUI()

  const theme = useTheme()
  const {
    bleedWrapper,
    drawer,
    root,
    stepper,
    stepOverviewContainer,
    numberStepsText,
    ordinalSelectMenu,
    ordinalSelector,
    locationButtonContainer,
    connector,
    closeButton,
  } = theme.components[`AppDirectionOverview`]?.styleOverrides || {}

  const { drawer: drawerProps } =
    theme.components[`AppDirectionOverview`]?.defaultProps || {}

  const { headerWrapper, paper, formControlLabel } =
    theme.components[`AppDirectionSearch`]?.styleOverrides || {}

  const { originIcon, destinationIcon, highlightedText } =
    theme.components[`DirectionStep`]?.styleOverrides || {}

  const destinationMarkerIcon = useMemo(
    () =>
      getIcon(theme?.assets?.directionStep?.destination, destinationIcon) || (
        <DestinationMarkerIcon
          sx={destinationIcon}
          color="error"
          fontSize="small"
        />
      ),
    [theme, destinationIcon]
  )

  const originMarkerIcon = useMemo(
    () =>
      getIcon(theme?.assets?.directionStep?.origin, originIcon) || (
        <OriginMarkerIcon sx={originIcon} />
      ),
    [theme, originIcon]
  )

  const highlightedStepText = (
    <Typography sx={highlightedText} variant="subTitle" component="span" />
  )

  const handleClickBack = useCallback(() => {
    goBack()
  }, [goBack])

  const handleOpenDrawer = useCallback(() => {
    setOpen(true)
  }, [])

  const handleCloseDrawer = useCallback(() => {
    setOpen(false)
  }, [])

  const handleChange = useCallback(
    ({ origin, destination }) => {
      const originParam = origin?.id || "unknown"
      const destinationParam = destination?.id || "unknown"

      navigate(`/maps/dir/${originParam}/${destinationParam}`, {
        replace: true,
      })
    },
    [navigate]
  )

  const handleClickStep = useCallback(() => setOpen((prev) => !prev), [])
  const stepsAvailable = overviewSteps && overviewSteps.length > 0

  const closeIcon = useMemo(
    () => getIcon(theme?.assets?.directionSearch?.closeIcon) || <CloseIcon />,
    [theme]
  )

  return (
    <>
      <Backdrop open={isCalculatingRoute}>
        <CircularProgress />
      </Backdrop>
      <Box sx={root}>
        <Box sx={headerWrapper}>
          <Stack spacing={2} alignItems="flex-start">
            <DirectionSearchContainer
              origin={originData}
              destination={destinationData}
              onChange={handleChange}
              onBack={handleClickBack}
              userLocation={userLocation}
              lastScannedLocation={_.last(scannedLocations)}
            />
            <Paper sx={paper} elevation={1}>
              <FormControlLabel
                sx={formControlLabel}
                control={
                  <Switch
                    checked={elevatorPreferredMode}
                    onChange={toggleElevatorPreference}
                  />
                }
                label={t("Avoid Steps")}
                componentsProps={{ typography: { variant: "body2" } }}
              />
            </Paper>
          </Stack>
        </Box>

        <Box sx={locationButtonContainer}>
          <MyLocationButton
            component={Link}
            to={`/maps/dir/unknown/${destinationData?.id}`}
          />
        </Box>

        {/* //TODO:: find a way for adding active ordinal (selectedOrdinal) */}
        {isMultiOrdinalVenue && (
          <Box sx={ordinalSelectMenu}>
            <OrdinalSelectMenu
              levels={relatedLevelWithSteps}
              onChangeOrdinal={changeOrdinal}
              selectedOrdinal={currentOrdinal}
            />
          </Box>
        )}
        {isMultiOrdinalVenue && (
          <Box sx={ordinalSelector}>
            <OrdinalSelector
              label="Floor"
              levels={relatedLevelWithSteps}
              onClickOrdinal={changeOrdinal}
              selectedOrdinal={currentOrdinal}
            />
          </Box>
        )}
      </Box>

      {stepsAvailable && (
        <>
          {/* Direction Summary (mobile) */}
          <SwipeableDrawer
            sx={drawer}
            {...drawerProps}
            open={open}
            onClose={handleCloseDrawer}
            onOpen={handleOpenDrawer}
            // contentProps={{ onTouchStart: () => {} }} This case will prevent the drawer from closing when dragging the drawer in the overview step to ensure the scrollbar in the DirectionStepItem section works properly
          >
            <Box sx={bleedWrapper}>
              <DirectionSummary
                duration={duration}
                distance={distance}
                originName={originName}
                destinationName={destinationName}
              />
              <DirectionCardActions
                onStart={startNavigate}
                onStep={handleClickStep}
              />
              <Button
                sx={closeButton}
                variant="contained"
                onClick={handleClickBack}
              >
                {closeIcon}
              </Button>
            </Box>

            <Box sx={stepOverviewContainer}>
              <Typography sx={numberStepsText} variant="body2">
                {t("NumberSteps", {
                  count: overviewSteps?.length || 0,
                })}
              </Typography>
              <Stepper
                sx={stepper}
                orientation="vertical"
                connector={<StepConnector sx={connector} />}
              >
                {originData && (
                  <DirectionStepItem
                    primaryLabel={
                      <Trans
                        components={{
                          location: highlightedStepText,
                        }}
                      >
                        {isMultiOrdinalVenue
                          ? t("Location Floor", {
                              location: t(originName),
                              level: originData.level,
                            })
                          : t(originName)}
                      </Trans>
                    }
                    secondaryLabel={t("Your last scanned location")}
                    stepLabelProps={{
                      icon: originMarkerIcon,
                    }}
                    venueLogo={originData.venueLogo}
                  />
                )}

                <StepConnector sx={connector} />

                {overviewSteps.map((step, index) => {
                  const { description } = step

                  return (
                    <DirectionStepItem
                      index={index}
                      navigateTo={`navigate/steps/${
                        index + 1
                      }?avoidSteps=${elevatorPreferredMode}`}
                      primaryLabel={
                        <Trans
                          components={{
                            category: highlightedStepText,
                            destination: highlightedStepText,
                            landmark: highlightedStepText,
                            intermediary: highlightedStepText,
                            toward: highlightedStepText,
                            turn: highlightedStepText,
                          }}
                        >
                          {t(description.text, {
                            category: t(description.options?.category),
                            destination: t(description.options?.destination),
                            landmark: t(description.options?.landmark),
                            intermediary: t(description.options?.intermediary),
                            toward: t(description.options?.toward),
                            turn: t(description.options?.turn),
                          })}
                        </Trans>
                      }
                      key={`step-description-${index}`}
                    />
                  )
                })}
                {destinationData && (
                  <DirectionStepItem
                    primaryLabel={
                      <Trans
                        components={{
                          location: highlightedStepText,
                        }}
                      >
                        {isMultiOrdinalVenue
                          ? t("Location Floor", {
                              location: t(destinationName),
                              level: destinationData.level,
                            })
                          : t(destinationName)}
                      </Trans>
                    }
                    secondaryLabel={t("Your Destination")}
                    stepLabelProps={{
                      icon: destinationMarkerIcon,
                    }}
                    venueLogo={destinationData.venueLogo}
                  />
                )}
              </Stepper>
            </Box>
          </SwipeableDrawer>
        </>
      )}
    </>
  )
}
